import { supabaseFetch } from './core';
import { devLog } from '../log';

// ============================================
// WORKOUT LOGS
// ============================================

export const getAthleteWorkoutLogs = async (athleteId, limit = 10) => {
  try {
    const logs = await supabaseFetch(
      `/workout_logs?athlete_id=eq.${athleteId}&select=id,completed_at,duration_minutes,notes,workout_id&order=completed_at.desc&limit=${limit}`
    );

    if (!logs || logs.length === 0) {
      return [];
    }

    // Prendi i dettagli dei workouts (filter out null workout_ids for free workouts)
    const workoutIds = logs.map(l => l.workout_id).filter(Boolean);
    let workouts = [];

    if (workoutIds.length > 0) {
      workouts = await supabaseFetch(`/workouts?id=in.(${workoutIds.join(',')})&select=id,name,day_number,program_id`) || [];
    }

    if (workoutIds.length === 0 || workouts.length === 0) {
      // All logs are free workouts or no workouts found
      return logs.map(log => ({
        id: log.id,
        completed_at: log.completed_at,
        duration_minutes: log.duration_minutes,
        notes: log.notes,
        workout: log.workout_id ? null : { id: null, name: 'Allenamento Libero', isFreeWorkout: true }
      }));
    }

    // Prendi i dettagli dei programmi
    const programIds = [...new Set(workouts.map(w => w.program_id).filter(Boolean))].join(',');
    const programs = programIds ? await supabaseFetch(`/programs?id=in.(${programIds})&select=id,name`) : [];

    // Combina i risultati
    return logs.map(log => {
      const workout = workouts.find(w => w.id === log.workout_id);
      if (!workout) {
        // Check if it's a free workout (workout_id is null)
        const isFreeWorkout = !log.workout_id;
        return {
          id: log.id,
          completed_at: log.completed_at,
          duration_minutes: log.duration_minutes,
          notes: log.notes,
          workout: isFreeWorkout
            ? { id: null, name: 'Allenamento Libero', isFreeWorkout: true }
            : null
        };
      }

      const program = programs.find(p => p.id === workout.program_id);

      return {
        id: log.id,
        completed_at: log.completed_at,
        duration_minutes: log.duration_minutes,
        notes: log.notes,
        workout: {
          ...workout,
          program: program || null
        }
      };
    });
  } catch (error) {
    console.error('❌ [DATABASE] getAthleteWorkoutLogs error:', error);
    return [];
  }
};

/**
 * Delete a workout log and all associated exercise logs (CASCADE)
 * @param {string} workoutLogId - The workout log UUID to delete
 * @param {string} athleteId - The athlete UUID (for verification)
 */
export const deleteWorkoutLog = async (workoutLogId, athleteId) => {
  try {
    /*#__PURE__*/ devLog('💾 [DATABASE] deleteWorkoutLog:', workoutLogId, 'for athlete:', athleteId);

    // Verify the workout log belongs to this athlete
    const logs = await supabaseFetch(`/workout_logs?id=eq.${workoutLogId}&athlete_id=eq.${athleteId}&select=id`);

    if (!logs || logs.length === 0) {
      throw new Error('Allenamento non trovato o non autorizzato');
    }

    // Delete the workout log (exercise_logs will be deleted automatically via CASCADE)
    await supabaseFetch(`/workout_logs?id=eq.${workoutLogId}`, {
      method: 'DELETE'
    });

    /*#__PURE__*/ devLog('✅ [DATABASE] deleteWorkoutLog successful');
    return true;
  } catch (error) {
    console.error('❌ [DATABASE] deleteWorkoutLog error:', error);
    throw error;
  }
};

export const getWorkoutLogDetails = async (workoutLogId) => {
  try {
    const logs = await supabaseFetch(`/workout_logs?id=eq.${workoutLogId}&select=*`);
    if (!logs || logs.length === 0) {
      throw new Error('Workout log not found');
    }

    const log = logs[0];
    const isFreeWorkout = !log.workout_id;
    let workout = null;

    // Prendi il workout con i suoi esercizi (solo se non è un allenamento libero)
    if (!isFreeWorkout) {
      const workouts = await supabaseFetch(`/workouts?id=eq.${log.workout_id}&select=id,name,day_number`);
      workout = workouts && workouts.length > 0 ? workouts[0] : null;

      if (workout) {
        const exercises = await supabaseFetch(`/exercises?workout_id=eq.${workout.id}&select=id,name,sets,reps,weight`);
        workout.exercises = exercises || [];
      }
    } else {
      // Per allenamenti liberi, crea un workout sintetico
      workout = {
        id: null,
        name: 'Allenamento Libero',
        isFreeWorkout: true,
        exercises: []
      };
    }

    // Prendi i log degli esercizi
    const exerciseLogs = await supabaseFetch(`/exercise_logs?workout_log_id=eq.${workoutLogId}&select=*`);

    // Find extra exercises (exercise_id is NULL, have exercise_template_id)
    // Per allenamenti liberi, TUTTI gli esercizi sono "extra" con exercise_template_id
    const extraExerciseLogs = (exerciseLogs || []).filter(log =>
      log.exercise_id === null && log.exercise_template_id
    );

    // Get unique template IDs for extra exercises
    const extraTemplateIds = [...new Set(extraExerciseLogs.map(log => log.exercise_template_id))];

    if (extraTemplateIds.length > 0 && workout) {
      // Load templates for extra exercises
      const templates = await supabaseFetch(
        `/exercise_templates?id=in.(${extraTemplateIds.join(',')})&select=id,name`
      );

      // Create synthetic exercise objects for extra exercises
      (templates || []).forEach(template => {
        const logsForThisTemplate = extraExerciseLogs.filter(
          log => log.exercise_template_id === template.id
        );

        // Use template ID as synthetic exercise ID
        const syntheticExercise = {
          id: `extra-template-${template.id}`,
          name: template.name,
          sets: Math.max(...logsForThisTemplate.map(l => l.set_number)),
          reps: '-',
          weight: null,
          isExtra: true,
          exercise_template_id: template.id
        };

        workout.exercises.push(syntheticExercise);
      });
    }

    return {
      ...log,
      workout,
      exercise_logs: exerciseLogs || []
    };
  } catch (error) {
    console.error('❌ [DATABASE] getWorkoutLogDetails error:', error);
    throw error;
  }
};

// ============================================
// EXERCISE LOGS
// ============================================

export const createExerciseLog = async (workoutLogId, exerciseId, setNumber, reps, weight, notes, isExtra = false, extraType = null, exerciseTemplateId = null, exerciseName = null, cardioData = null) => {
  try {
    // Validate required fields
    if (!workoutLogId) {
      throw new Error('Missing workout_log_id');
    }
    if (setNumber === undefined || setNumber === null) {
      throw new Error('Missing set_number');
    }

    // CONSTRAINT: exercise_id XOR exercise_template_id (one must be set, not both!)
    const hasExerciseId = exerciseId && !exerciseId.toString().startsWith('extra-');

    const logData = {
      workout_log_id: workoutLogId,
      exercise_id: hasExerciseId ? exerciseId : null,
      set_number: parseInt(setNumber) || 1,
      reps: reps !== null && reps !== undefined ? parseInt(reps) : null,
      weight: weight !== null && weight !== undefined ? parseFloat(weight) : null,
      notes: notes || null,
      is_extra: Boolean(isExtra),
      extra_type: extraType || null,
      exercise_template_id: hasExerciseId ? null : (exerciseTemplateId || null)
    };

    // Add cardio fields if provided
    if (cardioData) {
      logData.duration_minutes = cardioData.duration_minutes ? parseInt(cardioData.duration_minutes) : null;
      logData.speed = cardioData.speed ? parseFloat(cardioData.speed) : null;
      logData.incline = cardioData.incline ? parseFloat(cardioData.incline) : null;
      logData.distance = cardioData.distance ? parseFloat(cardioData.distance) : null;
    }

    /*#__PURE__*/ devLog('📝 [DATABASE] createExerciseLog data:', JSON.stringify(logData));

    const data = await supabaseFetch('/exercise_logs', {
      method: 'POST',
      body: JSON.stringify(logData),
    });
    return Array.isArray(data) ? data[0] : data;
  } catch (error) {
    console.error('❌ [DATABASE] createExerciseLog error:', error.message, '| exerciseId:', exerciseId, '| setNumber:', setNumber);
    throw error;
  }
};

export const updateExerciseLog = async (exerciseLogId, updates) => {
  try {
    const data = await supabaseFetch(`/exercise_logs?id=eq.${exerciseLogId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return Array.isArray(data) ? data[0] : data;
  } catch (error) {
    console.error('❌ [DATABASE] updateExerciseLog error:', error);
    throw error;
  }
};

export const deleteExerciseLog = async (exerciseLogId) => {
  try {
    await supabaseFetch(`/exercise_logs?id=eq.${exerciseLogId}`, {
      method: 'DELETE',
    });
    /*#__PURE__*/ devLog('✅ [DATABASE] deleteExerciseLog successful:', exerciseLogId);
  } catch (error) {
    console.error('❌ [DATABASE] deleteExerciseLog error:', error);
    throw error;
  }
};

/**
 * Check if an exercise has existing logs
 * Used to prevent deletion of exercises with history
 */
export const exerciseHasLogs = async (exerciseId) => {
  try {
    const logs = await supabaseFetch(
      `/exercise_logs?exercise_id=eq.${exerciseId}&select=id&limit=1`
    );
    return logs && logs.length > 0;
  } catch (error) {
    console.error('❌ [DATABASE] exerciseHasLogs error:', error);
    return false;
  }
};

/**
 * Get first performed data for multiple exercises (starting weight)
 * Returns { exerciseId: { weight, reps, date } }
 */
export const getFirstPerformedForExercises = async (athleteId, exerciseIds) => {
  try {
    /*#__PURE__*/ devLog('💾 [DATABASE] getFirstPerformedForExercises for', exerciseIds.length, 'exercises');

    if (!exerciseIds || exerciseIds.length === 0) {
      return {};
    }

    // Get athlete's workout logs ordered by date ASC
    const workoutLogs = await supabaseFetch(
      `/workout_logs?athlete_id=eq.${athleteId}&select=id,completed_at&order=completed_at.asc&limit=100`
    );

    if (!workoutLogs || workoutLogs.length === 0) {
      return {};
    }

    const workoutLogIds = workoutLogs.map(wl => wl.id).join(',');

    // Get exercise logs for these exercises from oldest workouts
    const exerciseLogs = await supabaseFetch(
      `/exercise_logs?workout_log_id=in.(${workoutLogIds})&exercise_id=in.(${exerciseIds.join(',')})&select=exercise_id,weight,reps,workout_log_id&order=workout_log_id.asc`
    );

    if (!exerciseLogs || exerciseLogs.length === 0) {
      return {};
    }

    // Map workout_log_id to date
    const workoutDateMap = {};
    workoutLogs.forEach(wl => {
      workoutDateMap[wl.id] = wl.completed_at;
    });

    // For each exercise, find the FIRST log with weight
    const firstPerformed = {};
    exerciseIds.forEach(exerciseId => {
      const logsForExercise = exerciseLogs.filter(log => log.exercise_id === exerciseId && log.weight);
      if (logsForExercise.length > 0) {
        const firstLog = logsForExercise[0]; // Already ordered by workout_log_id ASC
        firstPerformed[exerciseId] = {
          weight: firstLog.weight,
          reps: firstLog.reps,
          date: workoutDateMap[firstLog.workout_log_id]
        };
      }
    });

    /*#__PURE__*/ devLog('✅ [DATABASE] getFirstPerformedForExercises found', Object.keys(firstPerformed).length, 'starting weights');
    return firstPerformed;
  } catch (error) {
    console.error('❌ [DATABASE] getFirstPerformedForExercises error:', error);
    return {};
  }
};

/**
 * Delete all exercise logs for a specific session (all sets of an exercise in a workout)
 */
export const deleteSessionExerciseLogs = async (logIds) => {
  try {
    const ids = logIds.join(',');
    await supabaseFetch(`/exercise_logs?id=in.(${ids})`, {
      method: 'DELETE',
    });
    /*#__PURE__*/ devLog('✅ [DATABASE] deleteSessionExerciseLogs successful:', logIds.length, 'logs');
  } catch (error) {
    console.error('❌ [DATABASE] deleteSessionExerciseLogs error:', error);
    throw error;
  }
};

/**
 * Toggle excluded flag for all exercise logs in a session
 */
export const toggleExerciseLogExcluded = async (logIds, excluded) => {
  try {
    const ids = logIds.join(',');
    await supabaseFetch(`/exercise_logs?id=in.(${ids})`, {
      method: 'PATCH',
      body: JSON.stringify({ excluded }),
    });
    /*#__PURE__*/ devLog('✅ [DATABASE] toggleExerciseLogExcluded:', logIds.length, 'logs →', excluded);
  } catch (error) {
    console.error('❌ [DATABASE] toggleExerciseLogExcluded error:', error);
    throw error;
  }
};

// ============================================
// SALVATAGGIO SESSIONE (PB-013)
// Percorso UNICO scheda + libero: RPC save_workout_session, transazione
// atomica (workout_log + tutti gli exercise_logs) e idempotency DB-enforced
// su (athlete_id, client_session_id). Un retry (doppio tap, errore di rete,
// timeout dopo commit) ritorna la sessione già salvata senza duplicare nulla.
// Sostituisce la vecchia finestra di deduplicazione a 5 minuti (scheda) e il
// Promise.all di POST singoli senza rollback (libero).
// ============================================

/**
 * Mapper puro: exerciseLogs della SCHEDA → righe per l'RPC.
 * Preserva il comportamento storico: reps fallback 1 (cardio), exercise_id
 * XOR exercise_template_id (gli id "extra-" vanno sul template), tutti i
 * campi cardio inclusi.
 */
export const buildProgramLogRows = (exerciseLogs) =>
  exerciseLogs.map(log => {
    let repsValue = log.reps ? (typeof log.reps === 'string' ? parseInt(log.reps) : log.reps) : null;
    if (repsValue === null || repsValue === undefined) {
      repsValue = 1; // Fallback per cardio/esercizi speciali (reps NOT NULL a DB)
    }
    const weightValue = log.weight ? (typeof log.weight === 'string' ? parseFloat(log.weight) : log.weight) : null;
    const hasExerciseId = log.exerciseId && !log.exerciseId.toString().startsWith('extra-');
    return {
      set_number: parseInt(log.setNumber) || 1,
      reps: repsValue,
      weight: weightValue,
      notes: log.notes || null,
      is_extra: Boolean(log.isExtra),
      extra_type: log.extraType || null,
      exercise_id: hasExerciseId ? log.exerciseId : null,
      exercise_template_id: hasExerciseId ? null : (log.exerciseTemplateId || null),
      duration_minutes: log.duration_minutes ? parseInt(log.duration_minutes) : null,
      duration_seconds: log.duration_seconds ? parseInt(log.duration_seconds) : null,
      speed: log.speed ? parseFloat(log.speed) : null,
      incline: log.incline ? parseFloat(log.incline) : null,
      distance: log.distance ? parseFloat(log.distance) : null
    };
  });

/** Mapper puro: exerciseLogs del LIBERO → righe per l'RPC (comportamento storico). */
export const buildFreeLogRows = (exerciseLogs) =>
  exerciseLogs.map(log => {
    let repsValue = log.reps ? (typeof log.reps === 'string' ? parseInt(log.reps) : log.reps) : null;
    if (repsValue === null || repsValue === undefined) {
      repsValue = 1;
    }
    const weightValue = log.weight ? (typeof log.weight === 'string' ? parseFloat(log.weight) : log.weight) : null;
    return {
      exercise_id: null,
      set_number: log.setNumber,
      reps: repsValue,
      weight: weightValue,
      duration_seconds: log.durationSeconds ? parseInt(log.durationSeconds) : null,
      notes: log.notes || null,
      is_extra: true,
      extra_type: 'free_workout',
      exercise_template_id: log.exerciseTemplateId || null
    };
  });

/**
 * Chiama l'RPC transazionale. Ritorna { id, was_duplicate, inserted_logs }
 * (id = workout_log_id, contratto compatibile con i chiamanti esistenti).
 */
export const saveWorkoutSession = async ({ workoutId, durationMinutes, notes, logRows, clientSessionId }) => {
  const result = await supabaseFetch('/rpc/save_workout_session', {
    method: 'POST',
    body: JSON.stringify({
      p_workout_id: workoutId ?? null,
      p_duration_minutes: durationMinutes,
      p_notes: notes,
      p_logs: logRows,
      p_client_session_id: clientSessionId,
    }),
  });
  if (!result || !result.workout_log_id) {
    throw new Error('Failed to save workout session');
  }
  if (result.was_duplicate) {
    /*#__PURE__*/ devLog('✅ [DATABASE] Session already saved (idempotent retry), workout_log_id:', result.workout_log_id);
  }
  return { id: result.workout_log_id, was_duplicate: result.was_duplicate, inserted_logs: result.inserted_logs };
};

/**
 * Salva un workout completo (da scheda) in modo atomico e idempotente.
 * exerciseLogs: array di { exerciseId, setNumber, reps, weight, notes, completed }
 * clientSessionId: identità stabile della sessione (localStorage) — chiave di idempotency.
 */
export const saveCompleteWorkout = async (athleteId, workoutId, durationMinutes, notes, exerciseLogs, clientSessionId) => {
  try {
    /*#__PURE__*/ devLog('💾 [DATABASE] saveCompleteWorkout workout:', workoutId, 'logs:', exerciseLogs?.length, 'session:', clientSessionId);

    if (!exerciseLogs || exerciseLogs.length === 0) {
      throw new Error('No exercise logs to save');
    }

    const logRows = buildProgramLogRows(exerciseLogs);

    // Validazioni pre-invio (comportamento storico): XOR id/template e reps non nulli
    const invalidLogs = logRows.filter(log => {
      const hasExId = log.exercise_id !== null;
      const hasTemplateId = log.exercise_template_id !== null;
      return (hasExId && hasTemplateId) || (!hasExId && !hasTemplateId);
    });
    if (invalidLogs.length > 0) {
      console.error('❌ [DATABASE] Invalid logs found (constraint violation):', JSON.stringify(invalidLogs[0]));
      throw new Error('Dati esercizio non validi: manca exercise_id o exercise_template_id');
    }
    const nullRepsLogs = logRows.filter(log => log.reps === null || log.reps === undefined);
    if (nullRepsLogs.length > 0) {
      console.error('❌ [DATABASE] Found logs with null reps:', JSON.stringify(nullRepsLogs[0]));
      throw new Error('Errore interno: alcuni exercise logs hanno reps nullo. Contatta supporto.');
    }

    const saved = await saveWorkoutSession({ workoutId, durationMinutes, notes, logRows, clientSessionId });
    /*#__PURE__*/ devLog('✅ [DATABASE] saveCompleteWorkout successful, workout_log_id:', saved.id);
    return saved;
  } catch (error) {
    console.error('❌ [DATABASE] saveCompleteWorkout error:', error);
    throw error;
  }
};

// ============================================
// FREE WORKOUT (No predefined program)
// ============================================

/**
 * Salva un allenamento libero (workout_id = null) in modo atomico e idempotente.
 * @param {string} athleteId - Athlete UUID (l'RPC usa comunque auth.uid())
 * @param {number} durationMinutes - Durata in minuti
 * @param {string|null} notes - Note opzionali
 * @param {Array} exerciseLogs - Array di { exerciseTemplateId, exerciseName, setNumber, reps, weight, notes }
 * @param {string} clientSessionId - Identità stabile della sessione (chiave di idempotency)
 */
export const saveFreeWorkout = async (athleteId, durationMinutes, notes, exerciseLogs, clientSessionId) => {
  try {
    /*#__PURE__*/ devLog('💾 [DATABASE] saveFreeWorkout logs:', exerciseLogs?.length, 'session:', clientSessionId);

    if (!exerciseLogs || exerciseLogs.length === 0) {
      throw new Error('No exercise logs to save');
    }

    const logRows = buildFreeLogRows(exerciseLogs);
    const saved = await saveWorkoutSession({
      workoutId: null,
      durationMinutes,
      notes: notes || 'Allenamento libero',
      logRows,
      clientSessionId,
    });
    /*#__PURE__*/ devLog('✅ [DATABASE] saveFreeWorkout successful, workout_log_id:', saved.id);
    return saved;
  } catch (error) {
    console.error('❌ [DATABASE] saveFreeWorkout error:', error);
    throw error;
  }
};

// ============================================
// EXERCISE HISTORY FOR WORKOUT EXECUTION
// ============================================

/**
 * Get last performed data for multiple exercises (batch query for efficiency)
 * Used during workout execution to show "Last time: X kg × Y reps"
 * @param {string} athleteId - Athlete UUID
 * @param {Array<string>} exerciseIds - Array of exercise UUIDs
 * @returns {Object} - { exerciseId: { weight, reps, date } }
 */
export const getLastPerformedForExercises = async (athleteId, exerciseIds) => {
  try {
    /*#__PURE__*/ devLog('💾 [DATABASE] getLastPerformedForExercises for', exerciseIds.length, 'exercises');

    if (!exerciseIds || exerciseIds.length === 0) {
      return {};
    }

    // 1. Get all workout logs for athlete (last 3 months for efficiency)
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

    const workoutLogs = await supabaseFetch(
      `/workout_logs?athlete_id=eq.${athleteId}&completed_at=gte.${threeMonthsAgo.toISOString()}&select=id,completed_at&order=completed_at.desc`
    );

    if (!workoutLogs || workoutLogs.length === 0) {
      /*#__PURE__*/ devLog('⚠️ [DATABASE] No workout logs found');
      return {};
    }

    const workoutLogIds = workoutLogs.map(wl => wl.id).join(',');

    // 2. Get exercise logs for these exercises (include set_number and notes)
    let exerciseLogs = await supabaseFetch(
      `/exercise_logs?workout_log_id=in.(${workoutLogIds})&exercise_id=in.(${exerciseIds.join(',')})&select=exercise_id,weight,reps,set_number,notes,workout_log_id&order=workout_log_id.desc,set_number.asc`
    );

    if (!exerciseLogs) {
      exerciseLogs = [];
    }

    // FALLBACK: For exercises with no logs, try template_id
    if (exerciseLogs.length < exerciseIds.length) {
      const foundExerciseIds = new Set(exerciseLogs.map(log => log.exercise_id));
      const missingExerciseIds = exerciseIds.filter(id => !foundExerciseIds.has(id));

      if (missingExerciseIds.length > 0) {
        /*#__PURE__*/ devLog('⚠️ Missing logs for', missingExerciseIds.length, 'exercises, trying template fallback');

        // Get template_ids for missing exercises
        const exercisesData = await supabaseFetch(
          `/exercises?id=in.(${missingExerciseIds.join(',')})&select=id,exercise_template_id`
        );

        if (exercisesData && exercisesData.length > 0) {
          const templateIds = exercisesData
            .filter(e => e.exercise_template_id)
            .map(e => e.exercise_template_id);

          if (templateIds.length > 0) {
            const templateLogs = await supabaseFetch(
              `/exercise_logs?workout_log_id=in.(${workoutLogIds})&exercise_template_id=in.(${templateIds.join(',')})&select=exercise_id,exercise_template_id,weight,reps,set_number,notes,workout_log_id&order=workout_log_id.desc,set_number.asc`
            );

            if (templateLogs && templateLogs.length > 0) {
              // Map template logs back to exercise_id
              templateLogs.forEach(log => {
                const exercise = exercisesData.find(e => e.exercise_template_id === log.exercise_template_id);
                if (exercise) {
                  log.exercise_id = exercise.id; // Override for consistency
                  exerciseLogs.push(log);
                }
              });
              /*#__PURE__*/ devLog('✅ Found', templateLogs.length, 'additional logs via template fallback');
            }
          }
        }
      }
    }

    if (!exerciseLogs || exerciseLogs.length === 0) {
      /*#__PURE__*/ devLog('⚠️ [DATABASE] No exercise logs found');
      return {};
    }

    // 3. Create map of workout_log_id -> completed_at
    const workoutDateMap = {};
    workoutLogs.forEach(wl => {
      workoutDateMap[wl.id] = wl.completed_at;
    });

    // 4. For each exercise, find the most recent log with weight
    const result = {};

    exerciseIds.forEach(exerciseId => {
      // Get all logs for this exercise, sorted by date (most recent first)
      const logsForExercise = exerciseLogs
        .filter(log => log.exercise_id === exerciseId && log.weight && log.reps)
        .sort((a, b) => {
          const dateA = new Date(workoutDateMap[a.workout_log_id]);
          const dateB = new Date(workoutDateMap[b.workout_log_id]);
          return dateB - dateA;
        });

      if (logsForExercise.length > 0) {
        // Get most recent workout_log_id
        const lastWorkoutLogId = logsForExercise[0].workout_log_id;

        // Get ALL sets from that workout (not just first)
        const lastSessionSets = logsForExercise
          .filter(log => log.workout_log_id === lastWorkoutLogId)
          .sort((a, b) => a.set_number - b.set_number);

        result[exerciseId] = {
          weight: lastSessionSets[0].weight,
          reps: lastSessionSets[0].reps, // First set reps (backward compat)
          repsArray: lastSessionSets.map(s => s.reps), // NEW: All reps
          notesArray: lastSessionSets.map(s => s.notes).filter(Boolean), // NEW: All notes (filtered)
          date: workoutDateMap[lastWorkoutLogId]
        };
      }
    });

    /*#__PURE__*/ devLog('✅ [DATABASE] getLastPerformedForExercises successful, found data for', Object.keys(result).length, 'exercises');
    return result;
  } catch (error) {
    console.error('❌ [DATABASE] getLastPerformedForExercises error:', error);
    return {};
  }
};

/**
 * Ultimo dato usato per UN template (per l'aggiunta di esercizi extra).
 * Cerca i log di quel template (sia extra loggati con exercise_template_id,
 * sia log di esercizi in scheda che puntano allo stesso template) e ritorna
 * il set più recente. Ritorna null se non ci sono dati.
 * Shape: { weight, reps, duration_minutes, distance, date }
 */
export const getLastPerformedByTemplate = async (athleteId, templateId) => {
  try {
    if (!athleteId || !templateId) return null;

    // 1. Workout logs recenti dell'atleta (ultimi 6 mesi) → mappa data
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    const workoutLogs = await supabaseFetch(
      `/workout_logs?athlete_id=eq.${athleteId}&completed_at=gte.${sixMonthsAgo.toISOString()}&select=id,completed_at&order=completed_at.desc`
    );
    if (!workoutLogs || workoutLogs.length === 0) return null;

    const workoutLogIds = workoutLogs.map(wl => wl.id).join(',');
    const dateMap = {};
    workoutLogs.forEach(wl => { dateMap[wl.id] = wl.completed_at; });

    // 2. Esercizi in scheda che puntano a questo template (per i log via exercise_id)
    const exercisesWithTemplate = await supabaseFetch(
      `/exercises?exercise_template_id=eq.${templateId}&select=id`
    );
    const exerciseIds = (exercisesWithTemplate || []).map(e => e.id);

    // 3. Log per template_id (extra) + per exercise_id (scheda)
    const orParts = [`exercise_template_id.eq.${templateId}`];
    if (exerciseIds.length > 0) {
      orParts.push(`exercise_id.in.(${exerciseIds.join(',')})`);
    }
    const logs = await supabaseFetch(
      `/exercise_logs?workout_log_id=in.(${workoutLogIds})&or=(${orParts.join(',')})` +
      `&select=weight,reps,duration_minutes,distance,set_number,workout_log_id`
    );
    if (!logs || logs.length === 0) return null;

    // 4. Log più recente (per data del workout), primo set
    logs.sort((a, b) => {
      const da = new Date(dateMap[a.workout_log_id] || 0);
      const db = new Date(dateMap[b.workout_log_id] || 0);
      if (db - da !== 0) return db - da;
      return (a.set_number || 0) - (b.set_number || 0);
    });
    const last = logs[0];

    return {
      weight: last.weight ?? null,
      reps: last.reps ?? null,
      duration_minutes: last.duration_minutes ?? null,
      distance: last.distance ?? null,
      date: dateMap[last.workout_log_id] || null
    };
  } catch (error) {
    console.error('❌ [DATABASE] getLastPerformedByTemplate error:', error);
    return null;
  }
};
